import { useQuery, useMutation } from "@tanstack/react-query";
import { useRoute } from "wouter";
import { PublicReviewPage } from "@/components/PublicReviewPage";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { apiRequest } from "@/lib/queryClient";

interface PublicReviewData {
  business: {
    name: string;
    logoUrl?: string;
    brandColor?: string;
    googleReviewUrl?: string;
  };
  staff: {
    id: string;
    name: string;
  } | null;
}

export default function PublicReview() {
  const [, params] = useRoute("/r/:businessSlug/:staffSlug?");
  const businessSlug = params?.businessSlug || "";
  const staffSlug = params?.staffSlug || "";

  // Get reviewRequestId from URL query params to determine source
  const searchParams = new URLSearchParams(window.location.search);
  const reviewRequestId = searchParams.get("rid");
  const source = reviewRequestId ? "email" : "qr";

  const { data, isLoading, error } = useQuery<PublicReviewData>({
    queryKey: ["/api/public/review", businessSlug, staffSlug],
    queryFn: async () => {
      const url = staffSlug 
        ? `/api/public/review/${businessSlug}/${staffSlug}`
        : `/api/public/review/${businessSlug}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Business not found");
      }
      return response.json();
    },
    enabled: !!businessSlug,
  });

  const submitFeedbackMutation = useMutation({
    mutationFn: async ({ rating, feedback }: { rating: number; feedback: string }) => {
      return apiRequest("POST", "/api/public/feedback", {
        businessSlug,
        staffSlug: staffSlug || undefined,
        rating,
        feedback,
        source,
        reviewRequestId: reviewRequestId || undefined,
      });
    },
  });

  const handleSubmitFeedback = (rating: number, feedback: string) => {
    submitFeedbackMutation.mutate({ rating, feedback });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 px-6">
            <div className="flex flex-col items-center gap-4">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-16 w-16 rounded-full" />
              <Skeleton className="h-10 w-64" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 px-6 text-center">
            <h2 className="text-2xl font-semibold mb-2">Page Not Found</h2>
            <p className="text-muted-foreground">
              This review page doesn't exist or has been removed.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <PublicReviewPage
      businessName={data.business.name}
      logoUrl={data.business.logoUrl}
      staffName={data.staff?.name}
      googleReviewUrl={data.business.googleReviewUrl || "https://google.com"}
      brandColor={data.business.brandColor || "#3B82F6"}
      onSubmitFeedback={handleSubmitFeedback}
    />
  );
}
